import { ComponentProps } from 'react';
import styled from 'styled-components';
import { Box, BoxProps, BoxType, Flex } from 'reflexbox';

import * as graphql from '../../../../src/generated/graphql';
import { Skeleton } from './Skeleton';
import { CityIcon, FriendsIcon, GroupsIcon } from '@treams/treams-icons';

const AvatarContainer = styled(Box)<{ size?: number }>`
	img {
		background: ${(props) => props.theme.colors.lightBlue};
		display: block;
		width: ${(props) => props.size ?? 64}px;
		height: ${(props) => props.size ?? 64}px;
		border-radius: ${(props) => (props.size ?? 64) / 2}px;
	}
`;

const AvatarSkeletonContainer = styled(Skeleton.Box)<{ size?: number }>`
	width: ${(props) => props.size ?? 64}px;
	height: ${(props) => props.size ?? 64}px;
	border-radius: ${(props) => (props.size ?? 64) / 2}px;
`;

const InitialsContainer = styled(Flex)<{ size?: number }>`
	background: ${(props) => props.theme.colors.lightBlue};
	width: ${(props) => props.size ?? 64}px;
	height: ${(props) => props.size ?? 64}px;
	border-radius: ${(props) => (props.size ?? 64) / 2}px;
	align-items: center;
	justify-content: center;

	font-size: ${(props) => (props.size ?? 64) / 2}px;
	font-weight: bold;
	color: ${(props) => props.theme.colors.slateBlue};

	print-color-adjust: exact;
	-webkit-print-color-adjust: exact;
`;

const GroupContainer = styled(Flex)<{ group?: graphql.Group; size?: number }>`
	background: ${(props) =>
		props.group
			? props.group.type === graphql.GroupType.Department
				? props.theme.colors.manager
				: props.theme.colors.employee
			: props.theme.colors.primary};
	width: ${(props) => props.size ?? 64}px;
	height: ${(props) => props.size ?? 64}px;
	border-radius: ${(props) => (props.size ?? 64) / 2}px;
	align-items: center;
	justify-content: center;

	svg {
		display: block;
		width: ${(props) => ((props.size ?? 64) / 3) * 2}px;
		height: ${(props) => ((props.size ?? 64) / 3) * 2}px;

		path {
			fill: ${(props) => props.theme.colors.white};
		}
	}
`;

const OverflowContainer = styled(InitialsContainer)`
	background: transparent;
`;

const Initials = function Initials(props: {
	user: Pick<graphql.User, 'firstName' | 'lastName'>;
	size?: number;
}) {
	return (
		<InitialsContainer size={props.size}>
			{props.user.firstName[0]}
			{props.user.lastName[0]}
		</InitialsContainer>
	);
};

type UserAvatarView = ComponentProps<typeof Initials>['user'] & {
	avatar?:
		| {
				source: Pick<NonNullable<graphql.User['avatar']>['source'], 'url'>;
		  }
		| null
		| undefined;
};

function Avatar(props: BoxProps & { user: UserAvatarView; size?: number }) {
	const dim = ((props.size ?? 64) * 2).toFixed();

	return (
		<AvatarContainer {...props}>
			{'avatar' in props.user && props.user.avatar ? (
				<img
					src={props.user.avatar.source.url + '?d=' + [dim, dim].join('x')}
					alt={`${props.user.firstName} ${props.user.lastName}`}
				/>
			) : (
				<Initials size={props.size} user={props.user} />
			)}
		</AvatarContainer>
	);
}

function AvatarGroup(props: BoxProps & { group?: graphql.Group; size?: number }) {
	return (
		<GroupContainer {...props}>
			{props.group?.type === graphql.GroupType.Department && <GroupsIcon />}
			{props.group?.type === graphql.GroupType.Team && <FriendsIcon />}
			{props.group === undefined && <CityIcon />}
		</GroupContainer>
	);
}

Avatar.Group = AvatarGroup;

function AvatarSkeleton(props: BoxProps & { size?: number }) {
	return (
		<Box {...(props as ComponentProps<BoxType>)}>
			<AvatarSkeletonContainer size={props.size} />
		</Box>
	);
}

Avatar.Skeleton = AvatarSkeleton;

function AvatarOverflow(props: { count: number; size?: number }) {
	return <OverflowContainer size={props.size}>+{String(props.count)}</OverflowContainer>;
}

Avatar.Overflow = AvatarOverflow;

function AvatarList(props: { nodes: graphql.NoteCollaboratorEdge[]; limit?: number }) {
	const limit = props.limit ?? 3;
	const hasOverflow = props.nodes.length > limit + 1;
	const users = props.nodes.slice(0, hasOverflow ? limit : limit + 1);

	return (
		<>
			{users.map(({ node }) => (
				<Box key={node.id} mr={2}>
					<Avatar size={32} user={node} />
				</Box>
			))}

			{hasOverflow && (
				<Box mr={2}>
					<Avatar.Overflow size={32} count={props.nodes.length - limit} />
				</Box>
			)}
		</>
	);
}

Avatar.List = AvatarList;

export { Avatar };
